import { studyResponseSearchRequestSchema, studyResponseSearchResponseSchema } from './study-response-search.js';
import { studyAuthoringNextTestSelectionSchema } from './study-next-test-selection.js';
export { studyAuthoringNextTestSelectionSchema } from './study-next-test-selection.js';
import { resultQuestionSelectionSchema } from './shopify-study-results-view.js';
import { shopifyStudyResultsViewSchema } from './shopify-study-results-view.js';
import { shopifyStudyResultsResponseSchema, shopifySelectedStudyRunSchema } from './shopify-study-results-v1.js';
import { shopifyStudyRunStartRequestSchema, shopifyStudyRunStartResponseSchema } from './shopify-study-run-operation.js';
/** Shared lossless study authoring and merchant/operator CRUD contracts; no runtime app imports. */
import * as z from 'zod';
import { shopifyStudyBuilderDraftShapeSchema } from './shopify-study-builder-draft-operation.js';
import { shopifyAuthoredTreatmentTypeSchema } from './shopify-treatment-metadata.js';
import { resolveShopifyAudienceMode } from './shopify-audience-mode.js';
import { findStudyBuilderProhibitedField } from './study-builder-draft-validation.js';
/** Complete existing wire shape, with the same credential prohibition as legacy draft Save. */
export const studyAuthoringDraftSchema = shopifyStudyBuilderDraftShapeSchema.strict().superRefine((draft, ctx) => {
    const prohibited = findStudyBuilderProhibitedField(draft);
    if (prohibited)
        ctx.addIssue({ code: 'custom', message: `Prohibited secret field: ${prohibited}` });
    for (const key of ['testElements', 'surveyQuestions'])
        if (new Set(draft[key].map((item) => item.id)).size !== draft[key].length)
            ctx.addIssue({ code: 'custom', path: [key], message: 'IDs must be unique.' });
    try {
        resolveShopifyAudienceMode(draft.selectedAudienceMode, Boolean(draft.audienceDraft.selectedCustomerGroupLeverId));
    }
    catch (error) {
        if (!(error instanceof Error))
            throw error;
        ctx.addIssue({ code: 'custom', path: ['selectedAudienceMode'], message: error.message });
    }
});
export const studyAuthoringRecordSchema = z.strictObject({
    studyId: z.string().min(1),
    revision: z.number().int().positive(),
    draft: studyAuthoringDraftSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const studyAuthoringCreateSchema = z.strictObject({
    creationIntentId: z.string().trim().min(1).max(256),
    draft: studyAuthoringDraftSchema,
    nextTest: studyAuthoringNextTestSelectionSchema.optional(),
});
export const studyAuthoringReadSchema = z.strictObject({ studyId: z.string().min(1).max(256) });
export const studyAuthoringSaveSchema = studyAuthoringReadSchema.extend({
    expectedRevision: z.number().int().positive(),
    draft: studyAuthoringDraftSchema,
});
/** The server-chosen tone of a status badge/banner; the app never maps a status to a tone. */
export const studyAuthoringToneSchema = z.enum(['neutral', 'info', 'success', 'warning', 'critical']);
/** Server-derived readiness for the saved authoring revision; never a second editable document. */
export const studyAuthoringStateSchema = z.strictObject({
    status: z.enum(['draft', 'preparing', 'ready', 'running', 'complete', 'failed', 'blocked', 'deleting']),
    revision: z.number().int().positive(),
    stage: z.enum(['validation', 'projection', 'run', 'deletion']).nullable(),
    findings: z.array(z.strictObject({ code: z.string(), message: z.string() })),
    /** The merchant words and tone for this status; the badge and the builder banner both draw them. No app status map. */
    label: z.string().min(1),
    tone: studyAuthoringToneSchema,
    /** The builder's status banner, server-decided: its tone and which action button (if any) it offers. The banner's
     * heading is `label` and its lines are `findings`, so the app composes nothing; it wires the named action to a handler. */
    banner: z.strictObject({
        tone: z.enum(['info', 'critical']),
        action: z.enum(['review-and-run', 'view-results']).nullable(),
    }),
    /** What the form may not change for this status: typed text and/or structure. The app ORs these with its in-flight locks. */
    locks: z.strictObject({ text: z.boolean(), structure: z.boolean() }),
    /** How often the app should re-read this status (ms) because it is still moving; null when it is settled. */
    pollMs: z.number().int().positive().nullable(),
    /** Configuration is editable only before the first durably accepted parent Run. */
    canEdit: z.boolean(),
    canPrepare: z.boolean(),
    canRun: z.boolean(),
    canDelete: z.boolean(),
    latestRunId: z.string().nullable(),
    deletion: z.strictObject({ operationId: z.string(), status: z.enum(['accepted', 'cleaning', 'failed']) }).optional(),
});
export const studyAuthoringListSchema = z.strictObject({
    includeState: z.boolean().optional(),
    creationIntentId: z.string().trim().min(1).max(256).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    beforeStudyId: z.string().min(1).max(256).optional(),
});
export const studyAuthoringDeletionReceiptSchema = z.strictObject({
    studyId: z.string(),
    operationId: z.string(),
    status: z.enum(['accepted', 'cleaning', 'failed', 'completed']),
    /** The merchant words and tone for a retained cleanup receipt; the Tests list draws them, so the app maps no status. */
    label: z.string().min(1),
    tone: studyAuthoringToneSchema,
    lastErrorCode: z.string().nullable(),
    lastErrorMessage: z.string().nullable(),
    acceptedAt: z.string().optional(),
});
/** Read-only Tests facts. Accepted versions come from the frozen run; never count alternatives in the browser. */
export const studyAuthoringSummarySchema = z.strictObject({
    revision: z.number().int().positive(),
    runId: z.string().nullable(),
    treatmentType: shopifyAuthoredTreatmentTypeSchema.nullable(),
    versionCount: z.number().int().nonnegative().nullable(),
    versionCountError: z.enum(['invalid_comparison', 'snapshot_unreadable']).nullable(),
    result: z.discriminatedUnion('kind', [
        z.strictObject({ kind: z.literal('none'), label: z.string() }),
        z.strictObject({ kind: z.literal('decision'), decision: z.string(), label: z.string() }),
        z.strictObject({ kind: z.literal('unavailable'), reason: z.string(), label: z.string() }),
    ]),
});
export const studyAuthoringListResponseSchema = z.strictObject({
    studies: z.array(studyAuthoringRecordSchema),
    states: z.record(z.string(), studyAuthoringStateSchema).optional(),
    summaries: z.record(z.string(), studyAuthoringSummarySchema).optional(),
    deletions: z.array(studyAuthoringDeletionReceiptSchema).optional(),
    /**
     * The Worker-built starting draft for a brand-new "Create new test" visit: present only when the list was scoped to a
     * `creationIntentId` that has no saved study yet. The app renders it as the form's opening values (it never builds its
     * own defaults); `authoring`/`studies` stay empty, so the first Save still CREATEs rather than saving a phantom revision.
     */
    startingDraft: studyAuthoringDraftSchema.nullable().optional(),
    nextCursor: z.string().nullable(),
});
const auth = { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] };
const risk = { cost: 'free', destructive: false, dryRunCapable: false };
const reads = [{ kind: 'external-fetch', description: 'Signed Web connection-owned study authoring read' }];
const writes = [{ kind: 'external-fetch', description: 'Signed Web revision-checked study authoring write' }];
/** Read resolves saved work or the next creation identity, without fetching source or writing a draft. */
const studyAuthoringInsightSelectionSchema = z.strictObject({
    batchId: z.uuid(),
    productId: z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/),
});
const studyAuthoringInsightPreviewSchema = z.strictObject({
    creationIntentId: studyAuthoringCreateSchema.shape.creationIntentId,
    saved: studyAuthoringRecordSchema.nullable(),
});
export const SHOPIFY_STUDY_AUTHORING_INSIGHT_PREVIEW_OPERATION = {
    id: 'study-authoring.preview-from-insight',
    method: 'GET',
    path: '/api/study-authoring-from-insight',
    requestSchema: studyAuthoringInsightSelectionSchema,
    responseSchema: studyAuthoringInsightPreviewSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
/** Bind a confirmation to its original identity; an old request cannot create after a later deletion. */
export const studyAuthoringInsightSeedSchema = studyAuthoringInsightSelectionSchema.extend({
    creationIntentId: studyAuthoringCreateSchema.shape.creationIntentId,
    confirmed: z.literal(true),
});
export const SHOPIFY_STUDY_AUTHORING_INSIGHT_SEED_OPERATION = {
    id: 'study-authoring.seed-from-insight',
    method: 'POST',
    path: '/api/study-authoring-from-insight',
    requestSchema: studyAuthoringInsightSeedSchema,
    responseSchema: studyAuthoringRecordSchema,
    auth: { ...auth, requiresOfflineToken: true },
    risk,
    effects: writes,
    idempotent: false,
};
export const SHOPIFY_STUDY_AUTHORING_CREATE_OPERATION = {
    id: 'study-authoring.create',
    method: 'POST',
    path: '/api/study-authoring',
    requestSchema: studyAuthoringCreateSchema,
    responseSchema: studyAuthoringRecordSchema,
    auth,
    risk,
    effects: writes,
    idempotent: false,
};
export const SHOPIFY_STUDY_AUTHORING_READ_OPERATION = {
    id: 'study-authoring.read',
    method: 'GET',
    path: '/api/study-authoring',
    requestSchema: studyAuthoringReadSchema,
    responseSchema: studyAuthoringRecordSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
export const SHOPIFY_STUDY_AUTHORING_SAVE_OPERATION = {
    id: 'study-authoring.save',
    method: 'PUT',
    path: '/api/study-authoring',
    requestSchema: studyAuthoringSaveSchema,
    responseSchema: studyAuthoringRecordSchema,
    auth,
    risk,
    effects: writes,
    idempotent: false,
};
export const SHOPIFY_STUDY_AUTHORING_LIST_OPERATION = {
    id: 'study-authoring.list',
    method: 'GET',
    path: '/api/study-authoring-list',
    requestSchema: studyAuthoringListSchema,
    responseSchema: studyAuthoringListResponseSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
export const studyAuthoringStatusSchema = z.strictObject({
    authoring: studyAuthoringRecordSchema,
    state: studyAuthoringStateSchema,
});
export const SHOPIFY_STUDY_AUTHORING_PREPARE_OPERATION = {
    id: 'study-authoring.prepare',
    method: 'POST',
    path: '/api/study-authoring-prepare',
    requestSchema: studyAuthoringSaveSchema,
    responseSchema: studyAuthoringStatusSchema,
    auth: { ...auth, requiresOfflineToken: true },
    risk: { ...risk, cost: 'paid' },
    // The existing effect fingerprint fences old/new workers before compiling a differently scored instrument.
    effects: [
        ...writes,
        {
            kind: 'd1-write',
            description: 'Compile Pack Test v2 and Offer Test v1 questions with their registered anchor sets and primary metrics (#4720).',
        },
    ],
    idempotent: false,
};
export const SHOPIFY_STUDY_AUTHORING_STATUS_OPERATION = {
    id: 'study-authoring.status',
    method: 'GET',
    path: '/api/study-authoring-status',
    requestSchema: studyAuthoringReadSchema,
    responseSchema: studyAuthoringStatusSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
export const studyAuthoringDeleteSchema = studyAuthoringReadSchema.extend({ confirmed: z.literal(true) });
export const SHOPIFY_STUDY_AUTHORING_DELETE_OPERATION = {
    id: 'study-authoring.delete',
    successStatus: 202,
    method: 'POST',
    path: '/api/study-authoring-delete',
    requestSchema: studyAuthoringDeleteSchema,
    responseSchema: z.strictObject({
        studyId: z.string(),
        operationId: z.string(),
        status: z.enum(['accepted', 'cleaning', 'failed', 'completed']),
    }),
    auth,
    risk: { ...risk, destructive: true },
    effects: writes,
    idempotent: false,
};
export const studyAuthoringRunSchema = shopifyStudyRunStartRequestSchema
    .omit({ packetId: true })
    .extend({ studyId: z.string().min(1) });
export const SHOPIFY_STUDY_AUTHORING_RUN_OPERATION = {
    id: 'study-authoring.run',
    method: 'POST',
    path: '/api/study-authoring-run',
    requestSchema: studyAuthoringRunSchema,
    responseSchema: shopifyStudyRunStartResponseSchema,
    auth,
    risk: { cost: 'paid', destructive: false, dryRunCapable: true },
    effects: writes,
    idempotent: false,
};
export const studyAuthoringResultsSchema = studyAuthoringReadSchema.extend({
    runId: z.string().min(1).optional(),
    ...resultQuestionSelectionSchema.shape,
});
/** Internal composition input; rich saved evidence is projected once into the merchant view. */
export const directStudyResultsSourceSchema = shopifyStudyResultsResponseSchema.omit({ packet: true }).extend({
    product: z.strictObject({ id: z.string(), title: z.string() }),
});
/** Direct callers consume one complete presentation, not a second copy of every analysis and exchange. */
export const directStudyResultsSchema = directStudyResultsSourceSchema.extend({
    selectedRun: shopifySelectedStudyRunSchema.omit({ details: true, inquiry: true }).nullable(),
    view: shopifyStudyResultsViewSchema,
});
export const SHOPIFY_STUDY_AUTHORING_RESULTS_OPERATION = {
    id: 'study-authoring.results',
    method: 'GET',
    path: '/api/study-authoring-results',
    requestSchema: studyAuthoringResultsSchema,
    responseSchema: directStudyResultsSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
export const studyAuthoringDeletionStatusSchema = studyAuthoringReadSchema.extend({
    operationId: z.string().min(1).optional(),
});
export const SHOPIFY_STUDY_AUTHORING_DELETION_STATUS_OPERATION = {
    id: 'study-authoring.deletion-status',
    method: 'GET',
    path: '/api/study-authoring-deletion-status',
    requestSchema: studyAuthoringDeletionStatusSchema,
    responseSchema: studyAuthoringDeletionReceiptSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
/** Source-vector queries reuse accepted product answers and never call an embedding provider. */
export const SHOPIFY_STUDY_RESPONSE_SEARCH_OPERATION = {
    id: 'study-authoring.response-search',
    method: 'GET',
    path: '/api/study-response-search',
    requestSchema: studyResponseSearchRequestSchema,
    responseSchema: studyResponseSearchResponseSchema,
    auth,
    risk: { ...risk, dryRunCapable: true },
    effects: reads,
    idempotent: true,
};
//# sourceMappingURL=shopify-study-authoring-operation.js.map